import Layout from '@/components/Layout';
import LargeTourItem from '@/components/LargeTourItem';
import TourTypeContentPage from '@/components/TourTypeContent';
import { getMetaDataPage, getTourTypeContent, PageMetaOutput } from '@/services/directus';
import { tourService } from '@/services/tourService';
import { TourType } from '@/types/tourBooking';
import { Tour } from '@/types/tours';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { ReactElement } from 'react';

type ExperiencesPageProps = {
  content: any;
  leftContentHtml: string;
  rightContentHtml: string;
  tours: Tour[];
  metadata: PageMetaOutput;
};

const ExperiencesPage = ({
  content,
  leftContentHtml,
  rightContentHtml,
  tours,
  metadata,
}: ExperiencesPageProps) => {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
      </Head>
      <TourTypeContentPage
        content={content}
        leftContentHtml={leftContentHtml}
        rightContentHtml={rightContentHtml}
        sectionBackgroundColor="#A95A14"
      />
      <section>
        {tours.map((tour, index) => (
          <LargeTourItem
            key={tour.id}
            tour={tour}
            index={index}
            tourType="experiences"
          />
        ))}
      </section>
    </>
  );
};

ExperiencesPage.getLayout = (page: ReactElement) => (
  <Layout showTabs hasFooter hasPadding={false} headerBackgroundColor="#A95A14">
    {page}
  </Layout>
);

ExperiencesPage.type = 'public';

export async function getServerSideProps({ locale }: { locale: string }) {
  const lang = locale ?? 'en';
  const pageType = 'experience_tours';
  const i18nProps = await serverSideTranslations(lang, ['common', 'validate', 'aboutUs']);
  const normalizedLang = lang.split('-')[0];
  const i18nStore = (i18nProps as any)?._nextI18Next?.initialI18nStore;
  const localizedFallbackTitle =
    i18nStore?.[lang]?.common?.experienceTours?.metaTitle ??
    i18nStore?.[normalizedLang]?.common?.experienceTours?.metaTitle;
  const localizedFallbackDescription =
    i18nStore?.[lang]?.common?.experienceTours?.metaDescription ??
    i18nStore?.[normalizedLang]?.common?.experienceTours?.metaDescription;
  const fallbackTitle =
    typeof localizedFallbackTitle === 'string' && localizedFallbackTitle.trim()
      ? localizedFallbackTitle
      : 'HoiAnGO Experience Tours - Private Tours Around Hoi An';
  const fallbackDescription =
    typeof localizedFallbackDescription === 'string' && localizedFallbackDescription.trim()
      ? localizedFallbackDescription
      : 'Private Tours by E-Car - Discover a different view of Hoi An, with our Private Experience Tours. Our private tours by E-Car give an opportunity to explore the lush countryside and stunning coastal stretches and get up close to nature.';
  let metadata: PageMetaOutput = {
    title: fallbackTitle,
    description: fallbackDescription,
  };
  let leftContentHtml = '';
  let rightContentHtml = '';
  let content = null;
  let tours: Tour[] = [];

  try {
    metadata = await getMetaDataPage({
      type: pageType,
      fallbackTitle,
      fallbackDescription,
    });
  } catch (error) {
    console.error('[experiences] failed to load metadata', error);
  }

  try {
    content = await getTourTypeContent({
      lang,
      type: pageType,
    });
    leftContentHtml = content?.leftContentHtml ?? '';
    rightContentHtml = content?.rightContentHtml ?? '';
  } catch (error) {
    console.error('[experiences] failed to load page content', error);
  }

  try {
    tours = await tourService.fetchTours({
      lang,
      type: TourType.EXPERIENCE,
    });
  } catch (error) {
    console.error('[experiences] failed to load tours', error);
  }

  return {
    props: {
      content: content?.translation,
      leftContentHtml,
      rightContentHtml,
      tours,
      metadata,
      ...i18nProps,
    },
  };
}

export default ExperiencesPage;
